// Repeater UI for `z.array(...)` schemas: add / remove / reorder via
// up/down buttons; min/max badges; soft warning past max. **Fixed-slot
// mode** (when `exactLength` is set OR when `arrayOverrides[path]` is
// provided by the parent) hides add/remove/reorder and renders N
// labeled slots — used for big-3-shaped lists. Per-item rendering
// recurses via FieldRenderer at item path `${path}.${i}`; item labels
// are `${slotLabel || element.label || 'Item'} ${i + 1}` (the big-3
// "Meeting 1/2/3" label set comes from the override's slotLabel when
// the screen passes it, or `.describe('Meeting')` on the element when
// the schema declares it). Reorder UX: up/down per build-plan P-027;
// DnD V2-deferred.
//
// **Cross-schema dispatch via `arrayOverrides[path]` (P-032).** The
// Email editor passes `arrayOverrides={{ events: { fixedCount: 3,
// slotLabel: 'Meeting' } }}` when an `event-card-group` Block has
// `options.style === 'big-3'`. Per the P-027 forward note + P-032
// handoff §E recommendation (option (a)) — the dispatch fires here at
// the dispatch table's `array` renderer, not via a synthetic FieldNode
// walker patch. Override takes precedence over `node.exactLength` if
// both happen to be set.

import type { ArrayOverride, ArrayOverrides } from '../form'
import type { ArrayNode } from '../schema-to-fields'
import { FieldRenderer } from '../field-renderer'
import { defaultValueFor, removeAt, replaceAt, swap } from './list-field-helpers'

export interface ListFieldProps {
  readonly node: ArrayNode
  readonly value: unknown
  readonly path: string
  readonly idPrefix: string
  readonly errors: Readonly<Record<string, string>>
  readonly touched: ReadonlySet<string>
  readonly markTouched: (path: string) => void
  readonly onChange: (next: unknown) => void
  readonly arrayOverrides?: ArrayOverrides
  readonly blockId?: string
}

const ITEM_LABEL_FALLBACK = 'Item'

export function ListField({
  node,
  value,
  path,
  idPrefix,
  errors,
  touched,
  markTouched,
  onChange,
  arrayOverrides,
  blockId,
}: ListFieldProps) {
  const items: readonly unknown[] = Array.isArray(value) ? value : []
  const override: ArrayOverride | undefined = arrayOverrides?.[path]
  const exactLength = override?.fixedCount ?? node.exactLength
  const fixedSlot = exactLength !== undefined
  const itemCount = fixedSlot ? exactLength : items.length
  const minLength = node.minLength
  const maxLength = node.maxLength
  const canRemove = !fixedSlot && (minLength === undefined || items.length > minLength)
  const canAdd = !fixedSlot && (maxLength === undefined || items.length < maxLength)
  const overMax = !fixedSlot && maxLength !== undefined && items.length > maxLength
  const elementLabel = override?.slotLabel ?? (node.element.label || ITEM_LABEL_FALLBACK)

  return (
    <div class="flex flex-col gap-2">
      <div class="flex items-baseline justify-between gap-2">
        <span class="text-sm font-medium text-gray-800">
          {node.label}
          {node.required && (
            <span class="ml-0.5 text-red-600" aria-hidden="true">
              *
            </span>
          )}
        </span>
        <span class="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
          {badgeText(items.length, minLength, maxLength, exactLength)}
        </span>
      </div>
      <ul class="m-0 flex list-none flex-col gap-2 p-0">
        {Array.from({ length: itemCount }, (_, i) => {
          const itemLabel = `${elementLabel} ${i + 1}`
          return (
            <li key={i} class="flex flex-col gap-2 rounded border border-gray-200 bg-gray-50 p-3">
              <div class="flex items-center justify-between gap-2">
                <span class="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  {itemLabel}
                </span>
                {!fixedSlot && (
                  <div class="flex gap-1">
                    <ToolbarButton
                      ariaLabel={`Move ${itemLabel} up`}
                      onClick={() => onChange(swap(items, i, i - 1))}
                      disabled={i === 0}
                    >
                      ↑
                    </ToolbarButton>
                    <ToolbarButton
                      ariaLabel={`Move ${itemLabel} down`}
                      onClick={() => onChange(swap(items, i, i + 1))}
                      disabled={i === items.length - 1}
                    >
                      ↓
                    </ToolbarButton>
                    <ToolbarButton
                      ariaLabel={`Remove ${itemLabel}`}
                      onClick={() => onChange(removeAt(items, i))}
                      disabled={!canRemove}
                      variant="danger"
                    >
                      Remove
                    </ToolbarButton>
                  </div>
                )}
              </div>
              <FieldRenderer
                node={node.element}
                value={items[i]}
                path={`${path}.${i}`}
                idPrefix={idPrefix}
                errors={errors}
                touched={touched}
                markTouched={markTouched}
                onChange={(next) => onChange(replaceAt(items, i, next))}
                {...(arrayOverrides !== undefined ? { arrayOverrides } : {})}
                {...(blockId !== undefined ? { blockId } : {})}
              />
            </li>
          )
        })}
      </ul>
      {canAdd && (
        <button
          type="button"
          class="self-start rounded border border-blue-300 bg-blue-50 px-3 py-1 text-sm text-blue-700 hover:bg-blue-100"
          onClick={() => onChange([...items, defaultValueFor(node.element)])}
        >
          + Add {elementLabel}
        </button>
      )}
      {overMax && (
        <p class="m-0 text-xs text-amber-700" role="alert">
          Above maximum of {maxLength}; extra items may be dropped on save.
        </p>
      )}
    </div>
  )
}

function badgeText(
  count: number,
  minLength: number | undefined,
  maxLength: number | undefined,
  exactLength: number | undefined
): string {
  if (exactLength !== undefined) return `${exactLength} required`
  if (minLength !== undefined && maxLength !== undefined) {
    return `${count} of max ${maxLength} (min ${minLength})`
  }
  if (maxLength !== undefined) return `${count} of max ${maxLength}`
  if (minLength !== undefined) return `${count} (min ${minLength})`
  return `${count}`
}

interface ToolbarButtonProps {
  readonly ariaLabel: string
  readonly onClick: () => void
  readonly disabled: boolean
  readonly variant?: 'danger'
  readonly children: preact.ComponentChildren
}

function ToolbarButton({ ariaLabel, onClick, disabled, variant, children }: ToolbarButtonProps) {
  const variantClass =
    variant === 'danger'
      ? 'border-red-300 text-red-700 hover:bg-red-50'
      : 'border-gray-300 text-gray-700 hover:bg-gray-100'
  return (
    <button
      type="button"
      class={`rounded border bg-white px-2 py-0.5 text-xs disabled:cursor-not-allowed disabled:opacity-40 ${variantClass}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  )
}
